import { normaliseApiError } from 'core/services/apiService';

export const API_ERROR_EVENT = 'byod:api-error';

export type ApiErrorEventDetail = {
	message: string;
	isNetwork: boolean;
};

/** True when the browser/axios could not reach the server (offline, DNS, CORS block, timeout). */
export function isNetworkError(error: unknown): boolean {
	if (!error || typeof error !== 'object') return false;
	const e = error as any;
	if (e.code === 'ERR_NETWORK' || e.code === 'ECONNABORTED') return true;
	if (typeof e.message === 'string' && /network error/i.test(e.message)) return true;
	// Axios network failures have no response
	if (e.isAxiosError && !e.response) return true;
	if (e.response == null && e.request != null) return true;
	return false;
}

export function getUserFacingApiErrorMessage(error: unknown): string {
	if (isNetworkError(error)) {
		return 'Network error — check your connection and try again.';
	}
	return normaliseApiError(error).message || 'Something went wrong. Please try again.';
}

/** react-query retry: do not retry offline/network; at most one retry for other failures. */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
	if (isNetworkError(error)) return false;
	return failureCount < 1;
}

let lastNotifyAt = 0;
let lastNotifyMessage = '';

/**
 * Broadcast a user-visible API error (ToastContext listens).
 * Debounced so a page with many failing queries does not spam toasts.
 */
export function notifyApiError(error: unknown): void {
	if (typeof window === 'undefined') return;
	const message = getUserFacingApiErrorMessage(error);
	const now = Date.now();
	if (message === lastNotifyMessage && now - lastNotifyAt < 4000) return;
	lastNotifyAt = now;
	lastNotifyMessage = message;

	const detail: ApiErrorEventDetail = {
		message,
		isNetwork: isNetworkError(error),
	};
	window.dispatchEvent(new CustomEvent(API_ERROR_EVENT, { detail }));
}
